import React, { useState } from 'react';

const ReportsTab = ({ reports, handleReportAction }) => {
  const [notes, setNotes] = useState({});
  const [processingId, setProcessingId] = useState(null);

  const handleAction = async (reportId, action) => {
    setProcessingId(reportId);
    await handleReportAction(reportId, action, notes[reportId] || '');
    setProcessingId(null);
    setNotes(prev => ({ ...prev, [reportId]: '' }));
  };

  const getActionLabel = (action) => {
    switch(action) {
      case 'dismiss': return { text: 'Dismissed', color: 'text-gray-400', bg: 'bg-gray-800' };
      case 'moderate_post': return { text: 'Post Moderated', color: 'text-yellow-400', bg: 'bg-yellow-900/30' };
      case 'delete_post': return { text: 'Post Deleted', color: 'text-red-400', bg: 'bg-red-900/30' };
      default: return { text: 'Resolved', color: 'text-green-400', bg: 'bg-green-900/30' };
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Post Reports</h2>
        <div className="text-gray-400 font-mono text-sm">
          {reports.filter(r => r.status === 'pending').length} pending
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 p-8 rounded-sm text-center">
          <p className="text-gray-500 font-mono">No post reports</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => {
            const actionInfo = report.action_taken ? getActionLabel(report.action_taken) : null;
            
            return (
              <div 
                key={report.id} 
                className={`bg-gray-900 border p-6 rounded-sm ${
                  report.status === 'pending' 
                    ? 'border-red-800 bg-red-900/10' 
                    : 'border-gray-800'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs font-mono rounded uppercase ${
                        report.status === 'pending' 
                          ? 'bg-red-900/50 text-red-400 border border-red-800' 
                          : 'bg-gray-800 text-gray-400 border border-gray-700'
                      }`}>
                        {report.status}
                      </span>
                      <span className="text-gray-500 text-xs font-mono">
                        {new Date(report.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-white font-mono text-sm mb-1">
                      Post ID: <span className="text-yellow-400">{report.post_id}</span>
                    </p>
                    {report.reporter_email && (
                      <p className="text-gray-500 text-xs font-mono">
                        Reporter: {report.reporter_email}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-950 p-4 rounded mb-4">
                  <p className="text-gray-400 text-sm font-mono uppercase mb-2">Reason:</p>
                  <p className="text-white mb-3">{report.reason}</p>
                  
                  {report.posts && (
                    <>
                      <p className="text-gray-400 text-sm font-mono uppercase mb-2">Reported Post:</p>
                      <div className="bg-gray-900 p-3 rounded border border-gray-800">
                        <p className="text-gray-300 text-sm mb-1">{report.posts.content}</p>
                        {report.posts.user_name && (
                          <p className="text-gray-500 text-xs font-mono">By: {report.posts.user_name}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Show resolution info if resolved */}
                {report.status === 'resolved' && (
                  <div className={`p-4 rounded mb-4 ${actionInfo?.bg || 'bg-gray-800'} border border-gray-700`}>
                    <p className="text-gray-400 text-sm font-mono uppercase mb-2">Action Taken:</p>
                    <p className={`font-medium ${actionInfo?.color || 'text-gray-300'}`}>
                      {actionInfo?.text || 'Resolved'}
                    </p>
                    {report.admin_notes && (
                      <p className="text-gray-300 text-sm mt-2">Notes: {report.admin_notes}</p>
                    )}
                    {report.resolved_at && (
                      <p className="text-gray-500 text-xs font-mono mt-2">
                        Resolved: {new Date(report.resolved_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
                
                {report.status === 'pending' && (
                  <div className="space-y-3">
                    {/* Admin Notes */}
                    <div>
                      <label className="block text-xs font-mono text-gray-400 mb-1 uppercase">
                        Admin Notes (optional)
                      </label>
                      <textarea
                        value={notes[report.id] || ''}
                        onChange={(e) => setNotes(prev => ({ ...prev, [report.id]: e.target.value }))}
                        placeholder="Add notes about your decision..."
                        rows={2}
                        className="w-full px-3 py-2 bg-gray-950 border border-gray-700 text-white text-sm rounded-sm resize-none"
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAction(report.id, 'dismiss')}
                        disabled={processingId === report.id}
                        className="bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase"
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={() => handleAction(report.id, 'moderate_post')}
                        disabled={processingId === report.id}
                        className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase"
                      >
                        Moderate Post
                      </button>
                      <button
                        onClick={() => handleAction(report.id, 'delete_post')}
                        disabled={processingId === report.id}
                        className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase"
                      >
                        Delete Post
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
